import React from 'react';

/**
 * 전역 에러 바운더리 — "검은 화면" 방지의 마지막 방어선.
 *
 * 이게 없으면 렌더 중 예외 하나(지연 청크 로드 실패 포함)로 React가 트리 전체를
 * 언마운트해 유저는 새까만 화면만 보게 된다 (2026-07-14 크레딧 결제 후 실제 발생).
 *
 * - 청크 로드 실패(배포 직후 옛 세션이 사라진 해시 파일을 요청): 세션당 1회 자동 새로고침
 *   → 새 index.html과 새 청크 맵을 받아 스스로 복구된다.
 * - 그 외 렌더 크래시: 친절한 안내 + 새로고침 버튼 (침묵하는 검은 화면 금지)
 */

const CHUNK_ERROR_RE = /dynamically imported module|Loading chunk|Importing a module script failed|error loading dynamically imported/i;
const RELOAD_GUARD_KEY = 'sdl_chunk_reload_once';

interface State {
    hasError: boolean;
    isChunkError: boolean;
}

export class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
    state: State = { hasError: false, isChunkError: false };

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            isChunkError: CHUNK_ERROR_RE.test(error?.message ?? ''),
        };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        console.error('AppErrorBoundary가 잡은 오류:', error, info.componentStack);

        if (CHUNK_ERROR_RE.test(error?.message ?? '')) {
            // 배포로 청크 해시가 바뀐 케이스 — 무한 루프 방지 가드 하에 1회 자동 복구
            let alreadyReloaded = false;
            try {
                alreadyReloaded = sessionStorage.getItem(RELOAD_GUARD_KEY) === '1';
                if (!alreadyReloaded) sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
            } catch { /* 프라이빗 모드 등 — 그냥 1회 새로고침 */ }
            if (!alreadyReloaded) {
                window.location.reload();
            }
        }
    }

    private handleReload = () => {
        try { sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch { /* 무시 */ }
        window.location.reload();
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        return (
            <div
                className="min-h-screen bg-black flex items-center justify-center p-6"
                style={{ paddingTop: 'env(safe-area-inset-top)' }}
            >
                <div className="max-w-sm w-full text-center space-y-4">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#3182f6]/15 ring-1 ring-[#3182f6]/30 text-2xl">
                        ⚡
                    </div>
                    <h1 className="text-lg font-semibold text-white">
                        {this.state.isChunkError ? '새 버전으로 업데이트하고 있어요' : '화면을 불러오지 못했어요'}
                    </h1>
                    <p className="text-sm text-[#A8A8A8] leading-relaxed">
                        {this.state.isChunkError
                            ? '앱이 업데이트되어 새로고침이 필요해요. 잠시만 기다려주세요.'
                            : '일시적인 문제일 수 있어요. 아래 버튼으로 새로고침해주세요.'}
                    </p>
                    <button
                        onClick={this.handleReload}
                        className="w-full py-3.5 rounded-xl bg-[#3182f6] hover:bg-[#2b74dc] text-white text-sm font-semibold transition-colors"
                    >
                        새로고침
                    </button>
                </div>
            </div>
        );
    }
}

export default AppErrorBoundary;
